import React from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from 'lib/utils';
import EventResultWrapper from 'features/results/components/EventResultWrapper';
import { useEventResultsQuery } from 'features/results/queries';
import { useUserSessionContext } from 'features/session/userSessionStore';
import * as CheckWinsHook from 'features/tickets/hooks/check-wins-hook';
import { formatCurrency } from 'shared/money/formatCurrency';

function isNonEmptyObject(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  return Object.keys(value as any).length > 0;
}

export function ClosedEventPanel(props: {
  eventId: number | string;
  sportId?: number | string | null;
  scheduleId?: number | string | null;
  className?: string;
  /** Optional preloaded results payload (skips HTTP fetch when provided). */
  resultsOverride?: any;
  /** Optional event start time (Unix seconds) when not in results payload. */
  eventTimestamp?: number | string | null;
  /** For football week board: week number and week start time (Unix seconds). */
  weekNumber?: number | null;
  weekStartTime?: number | string | null;
}) {
  const { t } = useTranslation();
  const { eventId, scheduleId, className, resultsOverride, eventTimestamp: eventTimestampProp, weekNumber, weekStartTime } = props;
  const [{ user }] = useUserSessionContext() as any;
  const odds_format = user?.odds_format || 'decimal';

  const hasOverride = isNonEmptyObject(resultsOverride);
  const resultsQuery = useEventResultsQuery({
    eventId,
    scheduleId,
    enabled: !hasOverride,
  });
  const results = (hasOverride ? resultsOverride : resultsQuery.data) as any;
  const hasResults = isNonEmptyObject(results);

  const { win_results, loading: winsLoading, checkWinResults } = CheckWinsHook.useCheckWinResults();
  const didRequestWinsRef = React.useRef(false);

  React.useEffect(() => {
    if (!hasResults) return;
    if (!user) return;
    if (didRequestWinsRef.current) return;
    didRequestWinsRef.current = true;
    checkWinResults({ event_id: eventId });
  }, [hasResults, user, checkWinResults, eventId]);

  if (!hasOverride && resultsQuery.isLoading) {
    return (
      <div className={cn('p-4 text-sm text-muted-foreground', className)}>
        {t('LOADING') || 'Loading…'}
      </div>
    );
  }

  if (!hasOverride && resultsQuery.isError) {
    return (
      <div className={cn('p-4 text-sm text-muted-foreground', className)}>
        {t('ERROR_LOADING_DATA') || 'Failed to load data.'}
      </div>
    );
  }

  if (!hasResults) {
    // Event is not closed yet (results not available).
    return null;
  }

  const showWinBox = !!user;
  const ticketsCount = (win_results as any)?.tickets_count;
  const won = (win_results as any)?.won;
  const hasWinBoxContent =
    showWinBox && (winsLoading || won || ticketsCount === 0 || (ticketsCount > 0 && !won));

  const eventTimestamp = results?.event_timestamp ?? eventTimestampProp;

  return (
    <div className={cn('w-full', className)} data-event-results-panel={String(eventId)}>
      {/* Player outcome summary (only if logged in and there is something to show) */}
      {hasWinBoxContent && (
        <div className="px-2 py-2 border-b border-border">
          {winsLoading ? (
            <div className="text-xs text-muted-foreground">{t('LOADING') || 'Loading…'}</div>
          ) : (
            <>
              {won && (
                <div className="text-green-800 bg-green-100 p-2 rounded text-xs">
                  {t('TICKET_WON_MESSAGE', { total_wins: formatCurrency((win_results as any)?.total_wins, user?.currency) })}
                </div>
              )}
              {ticketsCount === 0 && (
                <div className="text-gray-700 bg-gray-100 p-2 rounded text-xs">
                  {t('NO_TICKET_MESSAGE') || 'No ticket for this event.'}
                </div>
              )}
              {ticketsCount > 0 && !won && (
                <div className="text-red-800 bg-red-100 p-2 rounded text-xs">
                  {t('TICKET_LOSS_MESSAGE') || 'Ticket lost.'}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Results */}
      <div className="py-2">
        <EventResultWrapper
          current_result={results}
          odds_format={odds_format}
          eventId={eventId}
          eventTimestamp={eventTimestamp}
          weekNumber={weekNumber}
          weekStartTime={weekStartTime}
        />
      </div>
    </div>
  );
}

